import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canViewServicer } from '../../lib/assetPermissions';

export const SAPI = process.env.REACT_APP_BACKEND_URL + '/api/servicers';
export const config = { withCredentials: true };
export const errorText = e => typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'No se pudo completar la operación';
export function useServicers() {
  const { user } = useAuth();
  const allowed = canViewServicer(user);
  const identity = `${user?.id || ''}:${user?.role || ''}`;
  const activeIdentity = useRef(null);
  const [items, setItems] = useState([]);
  const reload = useCallback(async () => {
    if (!allowed) return;
    try {
      const response = await axios.get(SAPI, config);
      if (activeIdentity.current === identity) setItems(response.data);
    } catch (e) { if (activeIdentity.current === identity) toast.error(errorText(e)); }
  }, [allowed, identity]);
  useEffect(() => {
    activeIdentity.current = identity;
    setItems([]);
    reload();
    return () => { activeIdentity.current = null; };
  }, [identity, reload]);
  return [allowed ? items : [], reload];
}
export const ServicerSelect = ({ items, value, onChange, id, empty = 'Sin SERVICER', disabled = false }) => <select data-testid={id} value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled} className="input-base min-w-0">
  <option value="">{empty}</option>{items.filter(s => s.activo || s.id === value).map(s => <option key={s.id} value={s.id} disabled={!s.activo}>{s.nombre}{s.activo ? '' : ' (inactivo)'}</option>)}
</select>;

export const AssetServicer = ({ asset, canEdit, onSaved }) => {
  const { user } = useAuth();
  const [items] = useServicers();
  const [value, setValue] = useState(asset.servicer_id || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(asset.servicer_id || ''); }, [asset.servicer_id]);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${SAPI}/activos/${asset.id}`, { servicer_id: value || null, revision: asset.servicer_revision || null }, config);
      toast.success('SERVICER actualizado'); onSaved();
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  if (!canViewServicer(user)) return null;
  return <div className="py-3 border-b border-slate-200 space-y-2 min-w-0" data-testid="summary-servicer">
    <div className="flex flex-col sm:flex-row sm:items-center gap-3"><span className="text-slate-500 shrink-0">SERVICER</span>
      {canEdit ? <div className="flex flex-wrap sm:ml-auto gap-2 min-w-0 w-full sm:w-auto"><div className="flex-1 min-w-0 sm:w-52"><ServicerSelect items={items} value={value} onChange={setValue} id="asset-servicer-select" disabled={busy} /></div><button className="btn-secondary text-xs" data-testid="asset-servicer-save" onClick={save} disabled={busy || value === (asset.servicer_id || '')}>Guardar</button></div> : <span data-testid="asset-servicer-name" className="font-medium">{items.find(s => s.id === value)?.nombre || 'Sin SERVICER'}</span>}
    </div>
    {asset.perimetro_reaparecido && <p className="text-amber-600 text-sm" data-testid="asset-servicer-reappeared">Reaparecido — pendiente de decidir reapertura</p>}
    {asset.perimetro_ultima_aparicion && <p className="text-xs" data-testid="asset-servicer-last-seen">Última aparición: {new Date(asset.perimetro_ultima_aparicion).toLocaleString('es-ES')}</p>}
    {asset.perimetro_ausencia_at && <p className="text-xs" data-testid="asset-servicer-absence">Ausencia detectada: {new Date(asset.perimetro_ausencia_at).toLocaleString('es-ES')}</p>}
    {asset.perimetro_cierre && <p className="text-xs" data-testid="asset-servicer-close-reason">{asset.perimetro_cierre.motivo}</p>}
    {(asset.perimetro_importacion_ausencia || asset.perimetro_ultima_importacion) && <Link className="text-xs text-blue-600" data-testid="asset-servicer-import-link" to={`/parametros/servicers?operacion=${asset.perimetro_importacion_ausencia || asset.perimetro_ultima_importacion}`}>Ver importación de referencia</Link>}
  </div>;
};